// Utility functions for date management in the meal planner

/**
 * Get the start of the week (Monday) for a given date.
 * NOTE: Monday-hardcoded and returns a Date. For configurable week starts use
 * getWeekStartISO below. These two are NOT interchangeable — see getWeekStartISO.
 */
export function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
}

/**
 * Week start as an ISO YYYY-MM-DD string, using the LOCAL day (weekStartsOn: 0=Sun..6=Sat).
 * Uses local midnight to avoid UTC day drift (e.g., Wed locally showing as Tue in UTC).
 * This is the canonical week-scoping key used across meal plan / shopping list.
 */
export function getWeekStartISO(d = new Date(), weekStartsOn = 1) {
  const cur = new Date(d);
  cur.setHours(0, 0, 0, 0);
  const dow = cur.getDay();
  const diff = (dow - weekStartsOn + 7) % 7;
  cur.setDate(cur.getDate() - diff);
  const y = cur.getFullYear();
  const m = String(cur.getMonth() + 1).padStart(2, '0');
  const day = String(cur.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Format a date range for the week label (e.g., "Week of Dec 16–22")
 */
export function formatWeekLabel(startDate) {
  // Treat the provided date as the actual start of the week
  // and compute a 7-day span ending 6 days later.
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  
  const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  
  // If same month, show "Week of Dec 16–22"
  if (startMonth === endMonth) {
    return `Week of ${startMonth} ${startDay}–${endDay}`;
  }
  
  // If different months, show "Week of Dec 30–Jan 5"
  return `Week of ${startMonth} ${startDay}–${endMonth} ${endDay}`;
}

/**
 * Get the day of the week as a short name (Mon, Tue, Wed, etc.)
 */
export function getDayShortName(date) {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[date.getDay()];
}

/**
 * Get the current day of the week as a short name
 */
export function getCurrentDayShortName() {
  return getDayShortName(new Date());
}

/**
 * Get a date by adding days to a start date
 */
export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Get a date by adding weeks to a start date
 */
export function addWeeks(date, weeks) {
  return addDays(date, weeks * 7);
}

/**
 * Format a date as ISO string (YYYY-MM-DD) for storage/comparison
 */
export function formatDateISO(date) {
  return date.toISOString().split('T')[0];
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1, date2) {
  return formatDateISO(date1) === formatDateISO(date2);
}

/**
 * Check if a date is today
 */
export function isToday(date) {
  return isSameDay(date, new Date());
}

/**
 * Get the date for a specific day of a week
 * @param weekStartDate The Monday of the week
 * @param dayName The day name (Mon, Tue, Wed, etc.)
 */
export function getDateForDay(weekStartDate, dayName) {
  const dayIndex = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(dayName);
  return addDays(weekStartDate, dayIndex);
}
